/**
 * Phase 46 (Plan 46-07) — Admin notification dispatch audit page.
 *
 * Per UI-SPEC §Surface 3: TopBar + intro paragraph + filter strip + table
 * (in Card) + side drawer for detail. Cursor-paginated 50 rows per page
 * via `useNotificationDispatchAudit` (useInfiniteQuery).
 *
 * Wrapped in `<AdminRoute>` at the router level (registered by the
 * orchestrator post-merge per parallel_warning).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TopBar } from '../../components/ui/TopBar';
import { Card } from '../../components/ui/Card';
import { Btn } from '../../components/ui/Btn';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { DropdownSelect } from '../../components/ui/DropdownSelect';
import { Spinner } from '../../components/common/Spinner';
import { StatusPill } from '../../components/notifications/StatusPill';
import { ChannelLabel } from '../../components/notifications/ChannelLabel';
import { EventKindLabel } from '../../components/notifications/EventKindLabel';
import { NotificationAuditDrawer } from '../../components/notifications/NotificationAuditDrawer';
import { useNotificationDispatchAudit } from '../../hooks/useNotificationDispatchAudit';
import type { AuditListFilters } from '../../services/api/notificationAudit';
import {
  EVENT_KINDS,
  CHANNELS,
  EVENT_KIND_LABELS,
  CHANNEL_LABELS,
  formatSkipReason,
  type EventKind,
  type Channel,
  type DispatchStatus,
} from '../../constants/notificationEnums';
import { DateField } from '../../components/ui/DateField';
import { formatDateTime } from '../../utils/dateFormat';

interface FilterFormState {
  projectId: string;
  eventKind: string;
  channel: string;
  status: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: FilterFormState = {
  projectId: '',
  eventKind: '',
  channel: '',
  status: '',
  from: '',
  to: '',
};

function toApiFilters(form: FilterFormState): AuditListFilters {
  const filters: AuditListFilters = {};
  const projectIdNum = form.projectId.trim() ? Number(form.projectId.trim()) : NaN;
  if (Number.isFinite(projectIdNum) && projectIdNum > 0) {
    filters.projectId = projectIdNum;
  }
  if (form.eventKind && (EVENT_KINDS as readonly string[]).includes(form.eventKind)) {
    filters.eventKind = form.eventKind as EventKind;
  }
  if (form.channel && (CHANNELS as readonly string[]).includes(form.channel)) {
    filters.channel = form.channel as Channel;
  }
  if (form.status === 'sent' || form.status === 'failed' || form.status === 'skipped') {
    filters.status = form.status as DispatchStatus;
  }
  if (form.from.trim()) {
    // Convert local YYYY-MM-DD to ISO start-of-day UTC. The native date input
    // gives us a date-only string; backend accepts ISO date-time.
    const d = new Date(`${form.from.trim()}T00:00:00Z`);
    if (!Number.isNaN(d.getTime())) filters.from = d.toISOString();
  }
  if (form.to.trim()) {
    const d = new Date(`${form.to.trim()}T23:59:59Z`);
    if (!Number.isNaN(d.getTime())) filters.to = d.toISOString();
  }
  return filters;
}

function formatRelative(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // MKS-314: a date+time belongs to the dateFormat spec, not to a locale
    // literal — `formatDateTime` is the helper every other audit-style
    // timestamp in the app already uses.
    return formatDateTime(d);
  } catch {
    return iso;
  }
}

export function NotificationsAuditPage() {
  const [form, setForm] = useState<FilterFormState>(EMPTY_FILTERS);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);

  const filters = useMemo(() => toApiFilters(form), [form]);
  const hasActiveFilters = useMemo(
    () =>
      Object.values(form).some((v) => v.trim() !== ''),
    [form],
  );

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useNotificationDispatchAudit(filters);

  const allRows = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data],
  );

  const clearFilters = () => setForm(EMPTY_FILTERS);

  return (
    <>
      <TopBar
        title="Notification dispatch audit"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Notifications audit' },
        ]}
      />
      <div className="p-6">
        <p className="text-[12px] text-ms-text-secondary mb-4">
          One row per (recipient × channel) dispatch attempt. Retained 90 days.
          Click a row to view the full state_change payload and error details.
        </p>

        {/* Filter strip */}
        <div className="flex flex-wrap items-end gap-3 mb-6">
          <Field label="Project">
            <TextInput
              type="number"
              min={1}
              value={form.projectId}
              onChange={(e) =>
                setForm((f) => ({ ...f, projectId: e.target.value }))
              }
              placeholder="All projects"
              aria-label="Project filter"
            />
          </Field>
          <Field label="Event kind">
            <DropdownSelect
              value={form.eventKind}
              onChange={(e) =>
                setForm((f) => ({ ...f, eventKind: e.target.value }))
              }
              aria-label="Event kind filter"
            >
              <option value="">All event kinds</option>
              {EVENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {EVENT_KIND_LABELS[k]}
                </option>
              ))}
            </DropdownSelect>
          </Field>
          <Field label="Channel">
            <DropdownSelect
              value={form.channel}
              onChange={(e) =>
                setForm((f) => ({ ...f, channel: e.target.value }))
              }
              aria-label="Channel filter"
            >
              <option value="">All channels</option>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {CHANNEL_LABELS[c].label}
                </option>
              ))}
            </DropdownSelect>
          </Field>
          <Field label="Status">
            <DropdownSelect
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value }))
              }
              aria-label="Status filter"
            >
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </DropdownSelect>
          </Field>
          <Field label="From">
            <DateField
              value={form.from}
              onChange={(nextDate) => setForm((f) => ({ ...f, from: nextDate }))}
              aria-label="From date filter"
            />
          </Field>
          <Field label="To">
            <DateField
              value={form.to}
              onChange={(nextDate) => setForm((f) => ({ ...f, to: nextDate }))}
              aria-label="To date filter"
            />
          </Field>
          {hasActiveFilters && (
            <Btn variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Btn>
          )}
        </div>

        <Card>
          {isLoading ? (
            <div className="py-6 flex justify-center">
              <Spinner />
            </div>
          ) : error ? (
            <div className="text-rose-600 text-[12.5px]">
              Could not load audit log: {error.message}. Refresh to try again.
            </div>
          ) : allRows.length === 0 ? (
            <div className="text-center py-8">
              <h3 className="text-[13px] font-semibold text-ms-text-strong">
                No dispatch attempts match these filters
              </h3>
              <p className="text-[12px] text-ms-text-secondary mt-1">
                Try widening the date range or clearing a filter.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-[12.5px] divide-y divide-ms-border-subtle">
                  <thead className="bg-ms-surface-2">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-ms-text-muted">
                      <th className="py-2 px-3 font-semibold">Dispatched at</th>
                      <th className="py-2 px-3 font-semibold">Project</th>
                      <th className="py-2 px-3 font-semibold">Event kind</th>
                      <th className="py-2 px-3 font-semibold">Recipient</th>
                      <th className="py-2 px-3 font-semibold">Channel</th>
                      <th className="py-2 px-3 font-semibold">Target</th>
                      <th className="py-2 px-3 font-semibold">Status</th>
                      <th className="py-2 px-3 font-semibold">Skip reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ms-border-subtle">
                    {allRows.map((row) => {
                      const isSelected = selectedRowId === row.id;
                      return (
                        <tr
                          key={row.id}
                          data-testid={`audit-row-${row.id}`}
                          className={`cursor-pointer ${isSelected ? 'bg-ms-orange/5' : 'hover:bg-ms-surface-2'}`}
                          onClick={() => setSelectedRowId(row.id)}
                        >
                          <td
                            className="py-2 px-3 tabular-nums"
                            title={row.dispatchedAt}
                          >
                            {formatRelative(row.dispatchedAt)}
                          </td>
                          <td className="py-2 px-3">
                            {/* Phase 103 (D-01): projectId is nullable for
                                three project-less producer families (kudos,
                                training, bot-usage). Render "Global" instead
                                of a broken /projects/null link when absent —
                                matching the recipient cell's null treatment
                                below. */}
                            {row.projectId !== null ? (
                              <Link
                                to={`/projects/${row.projectId}`}
                                className="text-ms-orange-dark hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                #{row.projectId}
                              </Link>
                            ) : (
                              <span className="italic text-ms-text-muted">
                                Global
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-ms-text-strong">
                            {/* Phase 103 (D-02): the audit row now carries
                                the real event kind. Fall back to the
                                state-change id for historical rows predating
                                the backfill, or any row a producer writes
                                without a kind. */}
                            {row.kind !== null ? (
                              <EventKindLabel kind={row.kind} />
                            ) : (
                              <span className="text-ms-text-muted">
                                State #{row.stateChangeId}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            {row.recipientId !== null ? (
                              <span className="text-ms-text-strong">
                                User #{row.recipientId}
                              </span>
                            ) : (
                              <span className="italic text-ms-text-muted">
                                (channel target)
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-ms-text-strong">
                            <ChannelLabel channel={row.channel} />
                          </td>
                          <td className="py-2 px-3">
                            {row.target ? (
                              <span
                                className="font-mono text-[11px] text-ms-text-strong truncate inline-block max-w-[16ch]"
                                title={row.target}
                              >
                                {row.target}
                              </span>
                            ) : (
                              <span className="text-ms-text-muted">—</span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            <StatusPill status={row.status} />
                          </td>
                          <td className="py-2 px-3 text-ms-text-secondary">
                            {formatSkipReason(row.skipReason)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {hasNextPage ? (
                <div className="mt-4 text-center">
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Btn>
                </div>
              ) : (
                <div className="mt-4 text-center text-[11px] text-ms-text-muted">
                  End of audit log
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <NotificationAuditDrawer
        open={selectedRowId !== null}
        auditRowId={selectedRowId}
        onClose={() => setSelectedRowId(null)}
      />
    </>
  );
}
